import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { ModuleButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { globals } from "@client/globals";
import withIsVisibleHOC, { type WithIsVisibleHOCProps } from "@client/hocs/withIsVisible";
import { setCookie } from "@client/lib/cookie";
import { type IFrameExitArgs, joinDefined } from "@client/lib/misc";
import Courses from "@common/components/Courses/component";
import { type ButtonProps } from "@common/controls/Button/component";
import { updateQueryString } from "@common/lib/url";
import * as React from "react";
import { type CourseList as CourseListTypes } from "../../types";

export type MapProps = RootContainerProps &
  WithIsVisibleHOCProps & {
    apiInfo: ModuleApiInfo;
    courses: CourseListTypes.Course[];
    courseStyle: React.CSSProperties;
    courseImageStyle: React.CSSProperties;

    isEnrolled: boolean;
  };

export type ActionProps = {
  onCourseClick: (id: string) => void;
  onMembershipCourseExit: (exitArgs: IFrameExitArgs | undefined) => void;
};

export type Props = ActionProps & MapProps;

export const CourseListComponent = (props: Props) => {
  const ButtonComponent = React.useMemo(
    () => (buttonProps: ButtonProps) => <ModuleButton {...getRootContainerProps(props)} {...buttonProps} />,
    [...getRootContainerPropsDep(props)]
  );

  // Workaround beause spaContext is not available here
  const goToNext = React.useCallback((portalId: string) => {
    setCookie("portalId", portalId, 365);
    if (window.location.pathname !== "/") {
      window.location.assign("/");
    } else {
      window.location.reload();
    }
  }, []);

  const onCourseClick = React.useCallback(
    (course: CourseListTypes.Course) => {
      if (globals.mode === "admin") {
        if (course.isMembership) {
          goToNext(course.portalId);
        } else {
          props.onCourseClick(course.id);
        }
      } else if (course.isMembership || !course.isEnrolled) {
        const customDomain = joinDefined(".", course.portalSubdomain, course.portalDomain);
        if (customDomain) {
          window.location.assign(`https://${customDomain}`);
        }
      } else {
        props.onCourseClick(course.id);
      }
    },
    [props.onCourseClick, goToNext]
  );

  return (
    <Courses
      getIframeSrc={(membershipCourse) =>
        Promise.resolve(
          updateQueryString(
            `${window.location.origin}${membershipCourse.isEnrolled ? "" : membershipCourse.registerPagePermalink}`,
            {
              portalId: membershipCourse.portalId,
              iframe: true
            }
          )
        )
      }
      courses={props.courses}
      courseStyle={props.courseStyle}
      courseImageStyle={props.courseImageStyle}
      isEnrolled={props.isEnrolled}
      onCourseClick={onCourseClick}
      onMembershipCourseExit={props.onMembershipCourseExit}
      ButtonComponent={ButtonComponent}
    />
  );
};

export default withIsVisibleHOC(CourseListComponent);
